export default {
  admin: {
    products: {
      holdingData: {
        deleteDataSingleDate: {
          button: "Delete",
          deleteSuccessText: "Holding data deleted successfully.",
          prompt: {
            delete: "Delete",
            text: (ctx) =>
              `You are about to permanently delete holding data for ${ctx.named("date")}. If you're sure, click the button below to confirm.`,
            title: "Delete holding data",
          },
        },
        deletePropertyMetadata: {
          deleteSuccessText: "Property deleted successfully.",
          prompt: {
            cancel: "Cancel",
            delete: "Delete",
            deleteForDate: (ctx) => `For date ${ctx.named("selectedDate")}`,
            deleteGlobally: "Globally",
            text: (ctx) =>
              `You are about to delete the property "${ctx.named("propertyToDelete")}". Would you like to delete it globally or only for this date?`,
            title: "Delete property",
          },
        },
        enrichDataSingleDate: {
          button: "Enrich from CSV",
          enrichSuccessText: "Holding data enriched successfully.",
          emptyCsv: "Nothing to import — the CSV is empty.",
          noPropertyToEnrich: "No user-defined property was found for enriching the current data.",
          enrichPrompt: {
            cancel: "Cancel",
            condition1:
              "The file must include a header row with at least: 'Holding name', 'Isin', 'Currency', and one additional user-defined property.",
            condition2:
              "Each row must have a unique combination of 'Holding name', 'Isin', and 'Currency'.",
            condition3:
              "Each combination of 'Holding name', 'Isin', and 'Currency' for the target date must appear exactly once in the CSV.",
            enrich: "Enrich",
            text: "Upload a CSV file to enrich the holding data. The following conditions must be met:",
            title: "Enrich holding data",
          },
          enrichNewPropertyPrompt: {
            cancel: "Cancel",
            enrich: "Enrich",
            isCategorical: "Categorical",
            isCategoricalTooltip:
              "Categorical properties allow holdings to be grouped and displayed in sections like 'Overview' and 'Factsheet', either on a specific date or over time. These properties can be translated in the Holding Data Adjustment section. If a categorical property is automatically generated (not user-defined), it can also be overridden in that section.",
            isFfill: "Forward filled",
            isFfillTooltip:
              "When no value is available for a selected date, the most recent preceding value will be used instead.",
            newProperty: "New property",
            text: "This enrichment will create new properties. Please review the proposed properties and define their default settings. Note: even minor differences in property names (e.g., case sensitivity) will result in the creation of new properties.",
            title: "New properties detected",
          },
          enrichErrorPrompt: {
            close: "Close",
            selectErrorType: "Select error type:",
            title: "Enrichment errors",
            columns: {
              currency: "Currency",
              firstRow: "First row",
              holdingName: "Holding name",
              isin: "ISIN",
              rowIndex: "Row index",
            },
            duplicatedHeader: {
              header: "Duplicated headers:",
              title: "Duplicated header",
              text: "Your CSV contains duplicated column headers. Please rename them so that each header is unique.",
            },
            emptyHeader: {
              title: "Empty header",
              text: "Your CSV contains empty column headers. Please assign a name to each column using at least one non-whitespace character.",
            },
            mismatchedColumn: {
              missingExpectedColumns: "Missing required columns:",
              title: "Column mismatch",
              text: "Ensure the CSV header includes (case-sensitive): Holding name, Isin, Currency.",
            },
            duplicatedConstituent: {
              title: "Duplicate constituent",
              text: "Remove duplicate entries for the same constituent.",
            },
            missingConstituent: {
              title: "Missing constituent",
              text: "Add missing constituents so the CSV fully covers the selected date.",
            },
            papaparseMissingQuotes: {
              title: "Unclosed quote",
              text: "CSV parsing failed because a quoted string was not properly closed. Please check for missing closing quotes in your data.",
            },
            papaparseUndetectableDelimiter: {
              title: "Undetectable delimiter",
              text: "CSV parsing failed. No common delimiter was detected. Please ensure your file consistently uses a single delimiter, typically a comma or semicolon.",
            },
            papaparseTooFewFields: {
              title: "Too few fields",
              text: "CSV parsing failed. Each row should have the same number of columns as there are headers. Currently, some rows have fewer columns than expected. Please check the structure of your file.",
            },
            papaparseTooManyFields: {
              title: "Too many fields",
              text: "CSV parsing failed. Each row should have the same number of columns as there are headers. Currently, some rows have more columns than expected. Please check the structure of your file.",
            },
          },
        },
        exportDataSingleDate: {
          button: "Export as CSV",
        },
        importDataSingleDate: {
          button: "Import from CSV",
          emptyCsv: "Nothing to import — the CSV is empty.",
          importSuccessText: "Holding data imported successfully.",
          importPrompt: {
            cancel: "Cancel",
            condition1:
              "The file must include a header row with at least: 'Holding name', 'Isin', 'Currency', 'value', and 'weight'.",
            condition2:
              "Each row must have a unique combination of 'Holding name', 'Isin', and 'Currency'.",
            condition3:
              "Each 'value' and 'weight' must be a number in a standard format (dot as decimal separator, no thousand separator).",
            date: "Date",
            import: "Import",
            text: "Upload a CSV to import holding data. The following conditions must be met:",
            title: "Import holding data",
          },
          importNewPropertyPrompt: {
            cancel: "Cancel",
            import: "Import",
            isCategorical: "Categorical",
            isCategoricalTooltip:
              "Categorical properties allow holdings to be grouped and displayed in sections like 'Overview' and 'Factsheet', either on a specific date or over time. These properties can be translated in the Holding Data Adjustment section. If a categorical property is automatically generated (not user-defined), it can also be overridden in that section.",
            isFfill: "Forward filled",
            isFfillTooltip:
              "When no value is available for a selected date, the most recent preceding value will be used instead.",
            newProperty: "New property",
            text: "This import will create new properties. Please review the proposed properties and define their default settings. Note: even minor differences in property names (e.g., case sensitivity) will result in the creation of new properties.",
            title: "New properties detected",
          },
          importErrorsPrompt: {
            close: "Close",
            selectErrorType: "Select error type:",
            title: "Import errors",
            columns: {
              currency: "Currency",
              firstRow: "First row",
              holdingName: "Holding name",
              isin: "ISIN",
              rowIndex: "Row index",
            },
            duplicatedHeader: {
              header: "Duplicated headers:",
              title: "Duplicated header",
              text: "Your CSV contains duplicated column headers. Please rename them so that each header is unique.",
            },
            emptyHeader: {
              title: "Empty header",
              text: "Your CSV contains empty column headers. Please assign a name to each column using at least one non-whitespace character.",
            },
            mismatchedColumn: {
              missingExpectedColumns: "Missing required columns:",
              title: "Column mismatch",
              text: "Ensure the CSV header includes (case-sensitive): Holding name, Isin, Currency, value, weight.",
            },
            duplicatedConstituent: {
              title: "Duplicate constituent",
              text: "Remove duplicate entries for the same constituent.",
            },
            missingValue: {
              title: "Missing value",
              text: "Some rows are missing a value. Ensure every row has a properly formatted numeric value.",
            },
            invalidValueFormat: {
              title: "Invalid value format",
              text: "Value format could not be inferred. Use dot as decimal separator and no thousand separator.",
            },
            missingWeight: {
              title: "Missing weight",
              text: "Some rows are missing a weight. Ensure every row has a properly formatted numeric weight.",
            },
            invalidWeightFormat: {
              title: "Invalid weight format",
              text: "Weight format could not be inferred. Use dot as decimal separator and no thousand separator.",
            },
            papaparseMissingQuotes: {
              title: "Unclosed quote",
              text: "CSV parsing failed because a quoted string was not properly closed. Please check for missing closing quotes in your data.",
            },
            papaparseUndetectableDelimiter: {
              title: "Undetectable delimiter",
              text: "CSV parsing failed. No common delimiter was detected. Please ensure your file consistently uses a single delimiter, typically a comma or semicolon.",
            },
            papaparseTooFewFields: {
              title: "Too few fields",
              text: "CSV parsing failed. Each row should have the same number of columns as there are headers. Currently, some rows have fewer columns than expected. Please check the structure of your file.",
            },
            papaparseTooManyFields: {
              title: "Too many fields",
              text: "CSV parsing failed. Each row should have the same number of columns as there are headers. Currently, some rows have more columns than expected. Please check the structure of your file.",
            },
          },
        },
        updatePropertyMetadata: {
          saveSuccessText: "Property updated successfully.",
          unsavedChangesAlert: "You have unsaved changes. Leaving now will discard them.",
          prompt: {
            isCategorical: "Categorical",
            isCategoricalTooltip:
              "Categorical properties allow holdings to be grouped and displayed in sections like 'Overview' and 'Factsheet', either on a specific date or over time. These properties can be translated in the Holding Data Adjustment section. If a categorical property is automatically generated (not user-defined), it can also be overridden in that section.",
            isFfill: "Forward filled",
            isFfillTooltip:
              "When no value is available for a selected date, the most recent preceding value will be used instead.",
            propertyForUrl: "URL Property",
            propertyForUrlTooltip:
              "Specify the name of the property containing the URL. The value of the current property will be used as the clickable text.",
            title: "Update property",
            translation: "Translation",
          },
        },
      },
    },
  },
  auth: {
    resetPasswordLink: {
      forgotPassword: "Forgot your password?",
    },
  },
  cards: {
    needLoginCard: {
      needLogged: "You need to be logged in to see this content.",
    },
  },
  compliance: {
    cookies: {
      buttonText: "Accept",
      cookiesUsage:
        "This Website uses Cookies to improve the usability. By using this Website you declare your consent for Cookies to be used as well as with the privacy policy where you can find more information on Cookies. Link: {0}.",
      privacyPolicy: "Privacy policy",
    },
  },
  components: {
    biggestDrawdownsTable: {
      rank: "Rank",
      startDate: "Start date",
      troughDate: "Trough date",
      recoveryDate: "Recovery date",
    },
    stressTestScenariosTable: {
      scenario: "Scenario",
      period: "Period",
      scenarioExtremelyBad: "Extremely bad",
      scenarioUnfavourable: "Unfavourable",
      scenarioModerate: "Moderate",
      scenarioFavourable: "Favourable",
    },
    liquidityRiskScenarioTable: {
      timeToLiquidate: "Time to liquidate",
      timeBucket1Day: "0-1 day",
      timeBucket2To7Days: "2-7 days",
      timeBucket8To30Days: "8-30 days",
      timeBucket31To90Days: "31-90 days",
      timeBucket91To180Days: "91-180 days",
      timeBucket181To365Days: "181-365 days",
      timeBucketMoreThan365Days: ">365 days",
      waterfallCumulativeWeight: "Waterfall cumulative weight",
      proRataCumulativeWeight: "Pro-Rata cumulative weight",
      nearProportionalCumulativeWeight: "Near-Proportional cumulative weight",
    },
    details: {
      report: {
        aumFormat: (ctx) =>
          `${ctx.named("currency")} ${ctx.named("aumFormatted")} million${ctx.named("aum") > 1000000 ? "s" : ""}${
            ctx.named("asOfDate") ? ` (as of ${ctx.named("asOfDate")})` : ""
          }`,
        lastPrice: (ctx) => `${ctx.named("currency")} ${ctx.named("lastPrice")}`,
      },
      overview: {
        aumFormat: (ctx) =>
          `${ctx.named("currency")} ${ctx.named("aumFormatted")} million${ctx.named("aum") > 1000000 ? "s" : ""} (as of ${ctx.named("asOfDate")})`,
        lastPrice: (ctx) =>
          `${ctx.named("currency")} ${ctx.named("lastPrice")} (as of ${ctx.named("asOfDate")})`,
      },
    },
    returnsVarGraph: {
      dailyReturn: "Daily Return",
      twoWeekReturn: "2-week Return",
      monthlyReturn: "Monthly Return",
      oneDayOnePercent: "1-Day VaR (99%)",
      oneDayFivePercent: "1-Day VaR (95%)",
      twoWeeksOnePercent: "2-Week VaR (99%)",
      twoWeeksFivePercent: "2-Week VaR (95%)",
      oneMonthOnePercent: "1-Month VaR (99%)",
      oneMonthFivePercent: "1-Month VaR (95%)",
    },
    download: {
      tooltip: "Download as image",
      prompt: {
        downloadPngButton: "Download as PNG",
        downloadSvgButton: "Download as SVG",
        text: "Choose which format you want to download the component as.",
        title: "Download component",
      },
    },
    portfolioBreakdownOnDate: {
      noDataYet: "This component has no data yet.",
    },
    portfolioBreakdownOverTime: {
      noDataYet: "This component has not enough data yet.",
    },
    statisticsWithPeriods: {
      noStatisticsToDisplay: "No statistics to display.",
    },
    statistics: {
      noStatisticsToDisplay: "No statistics to display.",
    },
    operations: {
      statuses: {
        failed: "Failed",
        pending: "Pending",
        running: "Running",
        succeeded: "Succeeded",
      },
    },
  },
  datePickers: {
    datePicker: {
      selectDateTooltip: "Select a date",
    },
    dateRangePicker: {
      from: "From (yyyy-mm-dd)",
      fullPeriod: "Full period",
      to: "To (yyyy-mm-dd)",
    },
    zoomPicker: {
      fiveYears: "5Y",
      fullPeriod: "MAX",
      oneMonth: "1M",
      oneYear: "1Y",
      sevenYears: "7Y",
      sixMonths: "6M",
      threeMonths: "3M",
      tenYears: "10Y",
      threeYears: "3Y",
      twoYears: "2Y",
      ytd: "YTD",
    },
  },
  dropdowns: {
    iconsDropdown: {
      noIcon: "No icon",
    },
  },
  editPrompt: {
    copyInAllLangs: "Copy in all languages",
    editField: "Edit field",
    enterText: "Enter a text",
    overwriteDataAllShareClasses: "Overwrite data of all share classes",
    propertyFilters: {
      title: "Property filters",
      tooltip:
        "To be taken into account, a holding must pass all applied filters. A holding passes a filter if it matches any of its selected values.",
      property: "Property",
      values: "Values",
      empty: "No property filter yet.",
      add: "Add property filter",
      multiSelect: {
        placeholder: "Select values",
        selectLabel: "Press enter to select",
        deselectLabel: "Press enter to remove",
        selectedLabel: "Selected",
        limitText: (ctx) => `and ${ctx.named("count")} more`,
      },
    },
    id: "ID",
    idTooltip:
      "This unique identifier serves as the reference for the component within the platform. Each component you create needs a distinct identifier. We recommend the following convention for enhanced readability: use the component's title, convert to lowercase, replace spaces with dash. For a component titled 'My Analytic Table', the identifier would be 'my-analytic-table'.",
    needUniqueId:
      "The provided identifier is currently in use by another component in the current location or in a linked one. Please supply a distinctive identifier to ensure uniqueness.",
    numberOfDecimals: "Number of decimals",
    numberOfDecimalsTooltip:
      "The number of decimals for all the numbers displayed in the component. Note that this does not include graph axes.",
    withBenchmark: "Show with the benchmark",
    resamplingStrategy: "Resampling strategy",
    windowHorizon: "Rolling window",
    windowHorizonTooltip:
      "The window horizon determines the 'look-back' period used to calculate annualized volatility at each point on the chart. A shorter horizon (e.g., 3 months) makes the chart highly responsive to recent market shocks and sudden regime shifts, though it may appear 'noisy' due to temporary fluctuations. Conversely, a longer horizon (e.g., 1 year) provides a smoother, more stable view of long-term risk by filtering out short-term market noise, though it will react more slowly to current events. Note that choosing a longer horizon will also result in a later starting date on the chart, as the calculation requires a full period of historical data before the first value can be plotted.",
    windowHorizonOptions: {
      threeMonths: "3 months",
      sixMonths: "6 months",
      oneYear: "1 year",
      twoYears: "2 years",
      threeYears: "3 years",
      fiveYears: "5 years",
      sevenYears: "7 years",
      tenYears: "10 years",
    },
    timeWindow: "Time window",
    timeWindowOptions: {
      threeMonths: "3 months",
      sixMonths: "6 months",
      oneYear: "1 year",
      twoYears: "2 years",
      threeYears: "3 years",
      fourYears: "4 years",
      fiveYears: "5 years",
      sixYears: "6 years",
      sevenYears: "7 years",
      eightYears: "8 years",
      nineYears: "9 years",
      tenYears: "10 years",
      all: "All",
    },
    valueAtRisk: "Value at risk",
    valueAtRiskOptions: {
      oneDayOnePercent: "1-Day VaR (99%)",
      oneDayFivePercent: "1-Day VaR (95%)",
      twoWeeksOnePercent: "2-Week VaR (99%)",
      twoWeeksFivePercent: "2-Week VaR (95%)",
      oneMonthOnePercent: "1-Month VaR (99%)",
      oneMonthFivePercent: "1-Month VaR (95%)",
    },
    footnote: "Text below component",
    placeholderFootnote: "A text that appears below the component. E.g. 'Fees are included.'",
    langTabError: "Some fields are invalid in the following languages:",
    settingsOnlyUsedOnOverviewTooltip: "This setting exclusively affects the 'Overview' view.",
    startDate: "Start date",
    startDateTooltip:
      "Select a start date (in YYYY-MM-DD format) for the component. The component will automatically adjust to display data starting from the earliest available date for the product or your chosen date, whichever is later. Leaving this field blank will display all available price data for the product.",
    tabError: "Some fields are invalid",
    title: "Title",
    titleEdit: (ctx) => `Edit ${ctx.named("componentId")}`,
    titleAdd: "Add component",
    unsavedChangesAlert:
      "You have unsaved changes. If you leave without saving, they will be lost.",
    resamplingStrategyOptions: {
      standard: "Standard",
      standardTooltip:
        "Automatically adjusts the level of detail based on your selected time range to keep the chart clear and readable.",
      monthly: "Monthly",
      monthlyTooltip: "Shows the last recorded value for each month in the selected period.",
    },
    reportFooter: {
      saveSuccess: "Footer was updated successfully.",
      title: "Footer",
      text: "Text",
      showPageNumber: "Show page number",
      showFooterLine: "Show footer line",
      margins: {
        bottom: "Bottom",
        left: "Left",
        right: "Right",
        title: "Margins (mm)",
      },
    },
    reportHeader: {
      backgroundImage: "Background image",
      backgroundImageTooltip:
        "The dimensions of the header is 794 pixels for width and whatever is chosen for height (see below), so use an image with dimensions following the same ratio for the best result.",
      logo: "Logo",
      saveSuccess: "Header was updated successfully.",
      showDateRange: "Show date range",
      textInPrimary: "Text in brand color",
      text: "Text",
      title: "Header",
      useCompanyLogo: "Use company logo",
      logoPosition: {
        left: "Left",
        right: "Right",
        title: "Logo position",
      },
      datePosition: {
        bottomLeft: "Bottom left",
        bottomRight: "Bottom right",
        centerLeft: "Center left",
        centerRight: "Center right",
        hidden: "Hidden",
        title: "Date position",
        topLeft: "Top left",
        topRight: "Top right",
      },
      headerVisibility: {
        allPages: "All pages",
        hidden: "Hidden",
        onlyFirstPage: "Only on the first page",
        title: "Header visibility",
      },
      height: {
        title: "Height",
        large: "Large (200 px / ~52.90 mm)",
        normal: "Normal (150 px / ~39.67 mm)",
        small: "Small (100 px / ~26.45 mm)",
        veryLarge: "Very large (250 px / ~66.12 mm)",
      },
      margins: {
        bottom: "Bottom",
        left: "Left",
        right: "Right",
        top: "Top",
        title: "Margins (mm)",
        tooltip: "Margins are not impacting the background image.",
      },
      productNamePosition: {
        bottomLeft: "Bottom left",
        bottomRight: "Bottom right",
        centerLeft: "Center left",
        centerRight: "Center right",
        hidden: "Hidden",
        title: "Product name position",
        topLeft: "Top left",
        topRight: "Top right",
      },
      textPosition: {
        bottomLeft: "Bottom left",
        bottomRight: "Bottom right",
        centerLeft: "Center left",
        centerRight: "Center right",
        title: "Text position",
        topLeft: "Top left",
        topRight: "Top right",
      },
    },
    reportStyle: {
      primaryColor: "Primary color",
      colorSchemePicker: "Color scheme picker",
      colorSchemeTooltip:
        "This color scheme currently applies to pie and donut charts. If no colors are provided, the charts default to the platform’s general color scheme.",
      saveSuccess: "Style was updated successfully.",
      title: "Style",
      fontSize: {
        normal: "Normal",
        large: "Large",
        small: "Small",
        title: "Font size",
      },
      margins: {
        left: "Left",
        right: "Right",
        title: "Margins (mm)",
      },
      spacing: {
        comfortable: "Comfortable",
        compact: "Compact",
        title: "Spacing",
      },
      titleBorder: {
        above: "Above the title",
        below: "Under the title",
        none: "No border",
        title: "Title Border",
      },
      titlesBold: {
        title: "Bold component titles",
      },
    },
    genericComponent: {
      addColumn: "Add column",
      addRow: "Add row",
      addData: "Add data",
      cantChangeType:
        "You can't change the type of a generic component once you have saved it. If you want a different type, you need to create a new generic component.",
      columnWithIndex: (ctx) => `Column ${ctx.named("idx")}`,
      data: "Data",
      date: "Date",
      displayValuesAsPerc: "Display values as percentage",
      displayValuesAsPercTooltip: "Displays values with a '%' symbol in the chart.",
      donutChart: "Donut chart",
      exampleSector: "E.g., Sectors",
      header: "Header",
      horizontalBarChart: "Horizontal bar chart",
      key: "Key",
      image: "Image",
      lineChart: "Line chart",
      needData:
        "You can't save a generic component without any data or only partial data. Delete it, add data or complete present data before saving.",
      pieChart: "Pie chart",
      removeData: "Remove data",
      showColumnHeader: "Show column header",
      table: "Table",
      text: "Text",
      type: "Type",
      value: "Value",
      verticalBarChart: "Vertical bar chart",
      video: "Video",
      weightPercent: "Weight (in %)",
      lineChartSection: {
        fileMust: "Your file must:",
        selectFile: "Select a file",
        uploadCsv: "Upload a CSV file containing your time series data.",
        conditions: {
          dateFormat: "Use the date format <strong>YYYY-MM-DD</strong>.",
          dateIncreaseAndUnique: "Contain dates that are strictly increasing and unique.",
          headerRow:
            "Include a header row with the columns <code>date</code> and <code>value</code>.",
          maxPoints:
            "Have a maximum of <strong>500 rows</strong> and a file size below <strong>1&nbsp;MB</strong>.",
          numberFormat:
            "Use a dot (.) as the decimal separator and do not include any thousand separators.",
        },
        errors: {
          exceedSize: {
            title: "File too large",
            text: "The uploaded file exceeds 1&nbsp;MB. Please limit it to a maximum of 500 data points.",
          },
          invalidData: {
            title: "Invalid data detected",
            text: "Some rows contain missing or invalid values. Make sure the <code>date</code> column uses the format <strong>YYYY-MM-DD</strong> and the <code>value</code> column contains valid numbers.",
          },
          invalidDataSequence: {
            title: "Invalid date sequence",
            text: "Dates must be strictly increasing and unique. Please correct your file and try again.",
          },
          parseFail: {
            title: "Parsing failed",
            text: "The CSV file could not be read correctly. Please verify its format and try again. If the issue persists, contact the Alquant team.",
          },
          tooManyDataPoints: {
            title: "Too many data points",
            text: "Your file contains more than 500 rows. Please reduce it to 500 or fewer before uploading.",
          },
          unexpectedError: {
            title: "Unexpected error",
            text: "An unexpected error occurred while processing your file. Please contact the Alquant team for assistance.",
          },
        },
      },
    },
    contacts: {
      addContact: "Add contact",
      contacts: "Contacts",
      contactsPerRow: "Contacts per row",
      additionalText: "Additional text",
      email: "Email",
      exampleEmail: "E.g., jdoe@mail.com",
      exampleName: "E.g., John Doe",
      examplePhone: "E.g., +41 79 123 45 67",
      exampleRole: "E.g., Partner",
      image: "Image",
      name: "Name",
      phone: "Phone",
      removeContact: "Remove contact",
      role: "Role",
    },
    dialog: {
      copyInAllLangs: {
        title: "Copy in all languages",
        text: "You are going to copy all the values of the current language to all other languages. Are you sure?",
      },
      saveFieldInAllShareClasses: {
        title: "Overwrite",
        text: "You are going to overwrite this field in all other share classes of this product, not only the current share class. Are you sure?",
      },
      saveInAllShareClasses: {
        title: "Overwrite",
        text: (ctx) =>
          `You are going to overwrite the "${ctx.named(
            "tabName"
          )}" data of all other share classes of this product with the "${ctx.named(
            "tabName"
          )}" data of this share class (not only the last changes you made). Are you sure?`,
      },
    },
    description: {
      contentTitle: "Description",
    },
    disclaimer: {
      contentTitle: "Disclaimer",
      defaultWarning:
        "You currently do not have a disclaimer for this product. Therefore, the standard disclaimer has been used. If you write a disclaimer here, it will only be displayed for this product.",
    },
    historicalPricesChart: {
      adjusted: "Use adjusted prices",
      adjustedTooltip:
        "Generally, adjusted prices mean prices that are adjusted for dividends, splits, and other events. Non-adjusted price simply refers to the cost of one share of the product at the end of the day.",
      defaultLogScale: "By default in log scale",
      showLogScaleSwitch: "Show log scale switch",
      priceIndexedTo100: "Use prices indexed to 100",
    },
    portfolioBreakdown: {
      cantChangeProperty:
        "You can't change the property of a holding component once you have saved it. If you want a different property, you need to create a new holding component.",
      chartType: "Chart type",
      displayOrder: "Display order",
      donutChart: "Donut chart",
      horizontalBarChart: "Horizontal bar chart",
      lagRule: "Data lag rule",
      lagRuleTooltip:
        "Choose how much to delay the data displayed in this component. For example, a two-month-end lag means showing the most recent data available from two months earlier, as of the end of that month. If today is January 15 2025, the data shown would be as of November 30 2024.",
      pieChart: "Pie chart",
      property: "Property",
      table: "Table",
      verticalBarChart: "Vertical bar chart",
      onlyShowTopThenOthers: {
        title: "Show top only",
        tooltip:
          'Only show the top X (chosen number), and merge the remaining ones in "Others". Let empty to show all.',
      },
      categoryIdsInOthers: {
        title: 'Elements to consider as "Others"',
        multiSelect: {
          placeholder: "Select elements",
          selectLabel: "Press enter to select",
          deselectLabel: "Press enter to remove",
          selectedLabel: "Selected",
          limitText: (ctx) => `and ${ctx.named("count")} more`,
        },
      },
      sortOptions: {
        alphabeticallyAscending: "Name (A → Z)",
        alphabeticallyDescending: "Name (Z → A)",
        weightAscending: "Weight (Low → High)",
        weightDescending: "Weight (High → Low)",
      },
      lagRuleOptions: {
        noLag: "No lag (use latest data)",
        oneMonthEnd: "Previous month-end data",
        twoMonthEnd: "Two-months-ago month-end data",
      },
      normalizeBreakdownWeight: "Normalize weights",
      normalizeBreakdownWeightTooltip:
        "When enabled, weights are scaled so they sum to 100% over all categories on the selected date (or over each date in the time series).",
    },
    keyCharacteristics: {
      addKeyCharacteristics: "Add key characteristics",
      exampleDynamicExposure: "E.g., Dynamic exposure",
      exampleWeDynamicallyAdapt: "E.g., We dynamically adapt ...",
      header: "Header",
      icon: "Icon",
      paragraph: "Paragraph",
      removeKeyCharacteristics: "Remove key characteristic",
    },
    biggestDrawdownsTable: {
      numberOfDrawdowns: "Number of drawdowns",
    },
    stressTestScenariosTable: {
      product: "Product",
      benchmark: "Benchmark",
      periods: "Periods",
      addPeriod: "Add period",
      deletePeriod: "Delete period",
    },
    liquidityAnalysisTable: {
      customizedTable: "Preview table",
      holdingsToIgnore: {
        title: "Holdings to ignore",
        holdingsMultiselect: {
          placeholder: "Select holdings to ignore",
          selectLabel: "Press enter to select",
          deselectLabel: "Press enter to remove",
          selectedLabel: "Selected",
          limitText: (ctx) => `and ${ctx.named("count")} more`,
        },
      },
      holdingsToIgnoreContainText: {
        title: "Ignore holdings whose name contains",
        placeholder: "e.g. Cash, Other",
      },
    },
    liquidityRiskScenarioTable: {
      rowsByTimeBucket: "Data by time to liquidate",
      amountPlaceholder: "Amount (Mio)",
      weightPlaceholder: "Weight (%)",
    },
    monthlyReturnsTable: {
      descending: "Sort years in descending order",
      benchmark: "Benchmark",
      product: "Product",
      return: "Return",
      nameReplacements: "Replace default names",
      nameReplacementsTooltip:
        "Customize the product and benchmark names in the component. Leave blank to default to the existing names.",
    },
    notify: {
      componentSaveSuccess: (ctx) =>
        `Your component '${ctx.named("componentId")}' was updated successfully.`,
    },
    overviewHeader: {
      backgroundImage: "Background image",
      backgroundImageTooltip:
        "Please be aware that the image currently under consideration is also utilized as the product card image on the homepage. Consequently, any alterations to this image will simultaneously affect the product's representation on the homepage.",
      hideProductName: "Hide product name",
      text: "Text",
      textInPrimary: "Text in brand color",
      textTooltip:
        "Please be aware that the text currently under consideration is also utilized as the product card text on the homepage. Consequently, any alterations to this text will simultaneously affect the product's representation on the homepage.",
      saveSuccess: "Header was updated successfully.",
      showDownloadLastFactsheetButton:
        'Show "@:components.extras.downloadLastFactsheet.downloadLastFactsheet" button',
      showDownloadLastFactsheetButtonTooltip:
        'For the button to be visible for a user, a document should be marked as "@:pages.products.documents.prompt.uploadFile.shouldConsiderLastFactsheet" within the "Documents" section, and it should be available in the selected language of the user.',
      showFollowButton: "Show follow button",
      title: "Edit overview header",
    },
    overviewStyle: {
      colorSchemePicker: "Color scheme picker",
      colorSchemeTooltip:
        "This color scheme currently applies to pie and donut charts. If no colors are provided, the charts default to the platform’s general color scheme.",
      saveSuccess: "Style was updated successfully.",
      title: "Style",
      titlesBold: {
        title: "Bold component titles",
      },
    },
    productDetails: {
      addDetail: "Add detail",
      exampleIsin: "E.g., ISIN",
      exampleLU: "E.g., LU2220388479",
      key: "Key",
      noteDragAndDrop: "Note: Drag and drop rows to reorder the details.",
      removeDetail: "Remove detail",
      showAgio: "Show agio",
      showAuM: "Show AuM",
      showLastPrice: "Show last price",
      showIsin: "Show ISIN",
      showTer: "Show TER",
      value: "Value",
    },
    relativePerformanceChart: {
      footnote: "Footnote",
      placeholderFootnote: "A text that appears below the chart. E.g. 'Fees are included.'",
    },
    riskProfile: {
      level: "Level of risk",
    },
    shareClassesTable: {
      bloomberg: "Bloomberg",
      class: "Class",
      column: "Column",
      columnsToShow: "Columns to display",
      showColumn: "Show column",
      defaultFootnote:
        "SI perf.: returns for since inception periods of more than one year are annualized, and not annualized if less than one year.",
      footnote: "Footnote",
      inceptionDate: "Inception date",
      isin: "ISIN",
      mgmtFee: "Mgmt. fee",
      minInvestment: "Min. invest.",
      perfFee: "Perf. fee",
      ter: "TER",
      title: "Edit share classes table",
    },
    statistics: {
      benchmark: "Benchmark",
      product: "Product",
      nameReplacements: "Replace default names",
      nameReplacementsTooltip:
        "Customize the product and benchmark names in the component. Leave blank to default to the existing names. For example, use a term like 'Product' to abbreviate and save display space.",
      fieldsToDisplay: "Fields to display",
      noteDragAndDrop: "Note: Drag and drop rows to reorder the statistics displayed.",
      shownStatisticName: "Shown statistic name",
      showStatistic: "Show statistic",
      statisticId: "Statistic id",
    },
    statisticsWithPeriods: {
      benchmark: "Benchmark",
      product: "Product",
      nameReplacements: "Replace default names",
      nameReplacementsTooltip:
        "Customize the product and benchmark names in the component. Leave blank to default to the existing names. For example, use a term like 'Product' to abbreviate and save display space.",
      fieldsToDisplay: "Fields to display",
      noteDragAndDrop: "Note: Drag and drop rows to reorder the statistics displayed.",
      periodsTitle: "Periods to show",
      shownStatisticName: "Shown statistic name",
      showStatistic: "Show statistic",
      statisticId: "Statistic id",
      periods: {
        1: "1M",
        3: "3M",
        6: "6M",
        ytd: "YTD",
        12: "1Y",
        36: "3Y",
        60: "5Y",
        84: "7Y",
        120: "10Y",
        max: "MAX",
      },
    },
    holdingsTable: {
      aggregateHoldings: "Aggregate holdings",
      aggregateHoldingsTooltip:
        "Aggregates the value and weight of holdings that share the same 'Holding name', 'Isin', and 'Currency'. All other properties are taken from the holding with the highest weight in the group. You can control this grouping in the 'Holding data adjustments' → 'Override' section by modifying any of these three fields.",
      fieldsToDisplay: "Fields to display",
      lagRule: "Data lag rule",
      lagRuleTooltip:
        "You can select if you want to lag the data shown on this component by a certain amount of time. For example, a two months end lag means you will show the latest available data from two month ago, at the end of the month. If you are the 15th of January 2025, this means the data displayed would be up to maximum the 30th of November 2024.",
      showHeader: "Show header",
      showHoldingsCount: 'Show "X out of Y holdings" when not all holdings are displayed',
      showTotal: "Show total",
      numHoldings: {
        title: "Number of holdings to show",
        tooltip:
          "If you leave this field blank, all available data for the product's holdings will be shown.",
      },
      holdingsToIgnore: {
        title: "Holdings to ignore",
        holdingsMultiselect: {
          placeholder: "Select holdings to ignore",
          selectLabel: "Press enter to select",
          deselectLabel: "Press enter to remove",
          selectedLabel: "Selected",
          limitText: (ctx) => `and ${ctx.named("count")} more`,
        },
      },
      holdingsToIgnoreContainText: {
        title: "Ignore holdings whose name contains",
        placeholder: "e.g. Cash, Other",
      },
      propertyFilters: {
        property: "Property",
        multiSelect: {
          placeholder: "Select values",
          selectLabel: "Press enter to select",
          deselectLabel: "Press enter to remove",
          selectedLabel: "Selected",
          limitText: (ctx) => `and ${ctx.named("count")} more`,
        },
      },
      lagRuleOptions: {
        noLag: "No lag (use latest data)",
        oneMonthEnd: "Previous month-end data",
        twoMonthEnd: "Two-months-ago month-end data",
      },
      displayOrder: "Display order",
      sortOptions: {
        alphabeticallyAscending: "Holding name (A → Z)",
        alphabeticallyDescending: "Holding name (Z → A)",
        weightAscending: "Weight (Low → High)",
        weightDescending: "Weight (High → Low)",
      },
      customizeTable: "Customize table",
      addColumn: "Add column",
      columnWithIndex: (ctx) => `Column ${ctx.named("idx")}`,
    },
  },
  extras: {
    changeRoles: {
      changeRoles: "Change roles",
      notify: {
        errorText: "No roles have been updated. Reason: ",
        successText: "User roles have been updated",
      },
    },
    deleteGenericComponent: {
      delete: "Delete",
      deleteAllComponents: "Delete all components",
      deleteOnlyThisComponent: "Delete only this component",
      deleting: "Deleting ...",
      deleteSuccess: "Deletion successful.",
      view: "View",
      shareClass: "Share class",
      textIsNotLinked:
        "You are about to permanently delete a generic component. This action cannot be undone. Please confirm that you wish to proceed with deletion.",
      textIsLinkedFirstPart:
        "You are about to permanently delete a generic component. This action cannot be undone.  Note that this component is linked with the same component in the following: ",
      textIsLinkedSecondPart:
        "Please confirm that you wish to proceed with the deletion, and decide if you wish to delete only the current component, or if you wish to delete this component and all the linked components.",
      title: (ctx) => `Permanent deletion of ${ctx.named("componentId")}`,
    },
    deleteUser: {
      button: "Delete",
      dialog: {
        confirm: "Delete user",
        text: "Are you sure you want to permanently delete this user?",
        title: "Delete user",
      },
      notify: {
        errorText: "User deletion failed.",
        successText: "User has been deleted.",
      },
    },
    downloadLastFactsheet: {
      downloadLastFactsheet: "Download latest factsheet",
      downloadingLastFactsheet: "Downloading...",
      needLogin: {
        text: "In order to download this document, you need to be authenticated.",
        title: "You need to be authenticated",
      },
    },
    manualVerifyEmail: {
      forceVerification: "Force email verification",
      verified: "Email verified",
      notify: {
        errorText: "Force email verification failed.",
        successText: "User email has been manually verified.",
      },
    },
    riskProfile: {
      higherRisk: "Higher risk",
      lowerRisk: "Lower risk",
    },
  },
  graphs: {
    logScale: "Log scale",
    notEnoughData: "Not enough data to plot this graph, please select a longer date range.",
    needMoreData:
      "To compute this metric, a certain amount of data at the beginning is necessary. Enlarge the timeframe to get access to this metric.",
  },
  holdingDataAdjustments: {
    categorise: {
      addCategory: {
        button: "Add category",
        prompt: {
          category: "Category",
          product: "Product",
          property: "Property",
          title: "Add a category",
          value: "Value",
          valueAlreadyHasCategory: "This value is already assigned to a category.",
        },
      },
      exportCategories: {
        button: "Export categories as CSV",
        buttonTooltip: "Download the shown category mappings as a CSV file.",
      },
      importCategories: {
        button: "Import categories from CSV",
        buttonTooltip: "Import a CSV file to update category mappings in bulk.",
        importPrompt: {
          buttonCancel: "Cancel",
          buttonImport: "Import",
          text: "Upload a correctly formatted CSV file to update your mappings. We recommend exporting the current mapping first to verify the column structure. You may upload a partial file containing only new or modified rows; unmentioned data will not be changed.",
          title: "Import categories via CSV",
        },
        importErrorsPrompt: {
          close: "Close",
          selectErrorType: "Select error type:",
          title: "CSV validation errors",
          columns: {
            firstRow: "First row",
            productName: "Product name",
            property: "Property",
            rowIndex: "Row index",
            value: "Value",
          },
          mismatchedColumn: {
            missingExpectedColumns: "The following required columns are missing:",
            notExpectedColumns: "The following unexpected columns were found:",
            title: "Column mismatch",
            text: 'Ensure the CSV header contains the following columns: "product_name, property, value, category".',
          },
          nonExistentProductName: {
            title: "Unknown product name",
            text: "Make sure all product names exist.",
          },
          invalidPropertyForProductName: {
            title: "Invalid property for product",
            text: 'Some properties are not available for the selected products. Remove them or review them in the "Holding Data" tab under the "Products" section in the admin panel.',
          },
          duplicatedCategory: {
            title: "Duplicate category for a value",
            text: "Remove duplicate category entries for the same value.",
          },
          papaparseMissingQuotes: {
            title: "Unclosed quote",
            text: "CSV parsing failed because a quoted string was not properly closed. Please check for missing closing quotes in your data.",
          },
          papaparseUndetectableDelimiter: {
            title: "Undetectable delimiter",
            text: "CSV parsing failed. No common delimiter was detected. Please ensure your file consistently uses a single delimiter, typically a comma or semicolon.",
          },
          papaparseTooFewFields: {
            title: "Too few fields",
            text: "CSV parsing failed. Some rows have fewer than the required 4 fields. Please verify the structure of your file.",
          },
          papaparseTooManyFields: {
            title: "Too many fields",
            text: "CSV parsing failed. Some rows have more than the expected 4 fields. Please verify the structure of your file.",
          },
        },
      },
      saveCategories: {
        button: "Save all changes",
        saveSuccess: "Holding data categories saved successfully.",
        updateWarningPrompt: {
          title: "Impact of saving changes",
          text: "You are about to save the categorisation after modifying one or more rows by removing or updating their category. If the affected categories have saved translations, those translations may be deleted — unless the category still exists for the same product through another value. Additionally, if these categories were used to override holding constituents, those overrides will also be removed — unless the category still exists for the same product through another value.",
        },
      },
    },
    translate: {
      exportTranslations: {
        button: "Export translations as CSV",
        buttonTooltip: "Download the shown translations mappings as a CSV file.",
      },
      importTranslations: {
        button: "Import translations from CSV",
        buttonTooltip: "Import a CSV file to update translations mappings in bulk.",
        importPrompt: {
          buttonCancel: "Cancel",
          buttonImport: "Import",
          text: "Upload a correctly formatted CSV file to update your mappings. We recommend exporting the current mapping first to verify the column structure. You may upload a partial file containing only new or modified rows; unmentioned data will not be changed.",
          title: "Import translations via CSV",
        },
        importErrorsPrompt: {
          close: "Close",
          selectErrorType: "Select error type:",
          title: "CSV validation errors",
          columns: {
            firstRow: "First row",
            language: "Language",
            productName: "Product name",
            property: "Property",
            rowIndex: "Row index",
            category: "Category",
          },
          mismatchedColumn: {
            missingExpectedColumns: "The following required columns are missing:",
            notExpectedColumns: "The following unexpected columns were found:",
            title: "Column mismatch",
            text: (ctx) =>
              `Ensure the CSV header contains the following columns: ${ctx.named("expectedColumns").join(", ")}.`,
          },
          nonExistentProductName: {
            title: "Unknown product name",
            text: "Make sure all product names exist.",
          },
          invalidPropertyForProductName: {
            title: "Invalid property for product",
            text: 'Some properties are not available for the selected products. Remove them or review them in the "Holding Data" tab under the "Products" section in the admin panel.',
          },
          duplicatedTranslations: {
            title: "Duplicate translations for a value",
            text: "Remove duplicate translations entries for the same value.",
          },
          invalidCategoryForProductNameAndProperty: {
            title: "Invalid category for product property",
            text: "The selected category does not exist for this property's product. Please verify the allowed categories for the given property.",
          },
          invalidTranslationLangForProductName: {
            title: "Unavailable language for product",
            text: "You cannot provide a translation in a language that the product is not configured to support. Please export the CSV and check a row for this product to see the available languages.",
          },
          papaparseMissingQuotes: {
            title: "Unclosed quote",
            text: "CSV parsing failed because a quoted string was not properly closed. Please check for missing closing quotes in your data.",
          },
          papaparseUndetectableDelimiter: {
            title: "Undetectable delimiter",
            text: "CSV parsing failed. No common delimiter was detected. Please ensure your file consistently uses a single delimiter, typically a comma or semicolon.",
          },
          papaparseTooFewFields: {
            title: "Too few fields",
            text: "CSV parsing failed. Each row should have the same number of columns as there are headers. Currently, some rows have fewer columns than expected. Please check the structure of your file.",
          },
          papaparseTooManyFields: {
            title: "Too many fields",
            text: "CSV parsing failed. Each row should have the same number of columns as there are headers. Currently, some rows have more columns than expected. Please check the structure of your file.",
          },
        },
      },
      saveTranslations: {
        button: "Save all changes",
        saveSuccess: "Holding data translations saved successfully.",
      },
    },
    override: {
      exportOverrides: {
        button: "Export overrides as CSV",
        buttonTooltip: "Download the shown overrides mappings as a CSV file.",
      },
      importOverrides: {
        button: "Import overrides from CSV",
        buttonTooltip: "Import a CSV file to update overrides mappings in bulk.",
        importPrompt: {
          buttonCancel: "Cancel",
          buttonImport: "Import",
          text: "Upload a correctly formatted CSV file to update your mappings. We recommend exporting the current mapping first to verify the column structure. You may upload a partial file containing only new or modified rows; unmentioned data will not be changed.",
          title: "Import overrides via CSV",
        },
        importErrorsPrompt: {
          close: "Close",
          selectErrorType: "Select error type:",
          title: "CSV validation errors",
          columns: {
            category: "Category",
            currency: "Currency",
            firstRow: "First row",
            holdingName: "Holding name",
            isin: "ISIN",
            productName: "Product name",
            property: "Property",
            rowIndex: "Row index",
          },
          mismatchedColumn: {
            missingExpectedColumns: "The following required columns are missing:",
            notExpectedColumns: "The following unexpected columns were found:",
            title: "Column mismatch",
            text: (ctx) =>
              `Ensure the CSV header contains the following columns: ${ctx.named("expectedColumns").join(", ")}.`,
          },
          unknownProductName: {
            title: "Unknown product name",
            text: "Some product names in your file do not match any existing products. Please check for typos or invalid names.",
          },
          invalidPropertyForProductName: {
            title: "Invalid property for product",
            text: 'Some properties are not available for the selected products. Remove them or review them in the "Holding Data" tab under the "Products" section in the admin panel.',
          },
          unknownOverrideCategory: {
            title: "Unknown override category",
            text: "One or more override values refer to a non-existent category for the specified product and property. Please verify available categories before importing.",
          },
          invalidConstituent: {
            title: "Invalid constituent key",
            text: "Some rows refer to constituents (Product Name, Holding Name, ISIN, Currency) that do not exist. Please remove or correct these entries.",
          },
          duplicateConstituent: {
            title: "Duplicate overrides",
            text: "Multiple override entries were found for the same constituent (Product Name, Holding Name, ISIN, Currency). Please keep only one entry per constituent.",
          },
          papaparseMissingQuotes: {
            title: "Unclosed quote",
            text: "CSV parsing failed because a quoted string was not properly closed. Please check for missing closing quotes in your data.",
          },
          papaparseUndetectableDelimiter: {
            title: "Undetectable delimiter",
            text: "CSV parsing failed. No common delimiter was detected. Please ensure your file consistently uses a single delimiter, typically a comma or semicolon.",
          },
          papaparseTooFewFields: {
            title: "Too few fields",
            text: "CSV parsing failed. Each row should have the same number of columns as there are headers. Currently, some rows have fewer columns than expected. Please check the structure of your file.",
          },
          papaparseTooManyFields: {
            title: "Too many fields",
            text: "CSV parsing failed. Each row should have the same number of columns as there are headers. Currently, some rows have more columns than expected. Please check the structure of your file.",
          },
        },
      },
      saveOverrides: {
        button: "Save all changes",
        saveSuccess: "Holding data overrides saved successfully.",
      },
    },
  },
  images: {
    uploadImage: {
      darkening: "Darkening",
      file: "File",
      noFile: "No file",
      noImage: "No image",
      promptTitle: "Upload new image",
      selectFile: "Select file",
      selectNewImage: "Select new image",
      submit: "Submit",
      submitting: "Submitting...",
      errorTitleTooLarge: "Image too large",
      errorMessageTooLarge:
        "The image is too large. Please, select another image with a size smaller than 30 MB.",
      warningTitleVeryLarge: "Image very large",
      warningMessageVeryLarge:
        "The image is very large. It will make the pages displaying this image slower to load. For best performance, it is advised to use images smaller than 10 MB.",
    },
  },
  linkPrompt: {
    componentLinkedTo: "This component is linked to:",
    currentView: "Current view",
    currentShareClass: "Current share class",
    linkThroughViewsTooltip:
      'Enabling this feature creates a synchronized connection between components across multiple views. For instance, linking the "Factsheet" and "Overview" views ensures any edits to a shared component are updated on both views simultaneously. Important: The content of the linked component is duplicated from the source view you\'re currently editing. If you initiate the link while editing the "description" component of the "Factsheet" view, the "description" component in "Factsheet" is copied to the "Overview" and will thus override the "description" component of "Overview".',
    linkThroughShareClassesTooltip:
      'Enabling this feature creates a synchronized connection between components across multiple share classes. For instance, linking the "My Product Share A" and "My Product Share B" ensures any edits to a shared component are updated on both share classes simultaneously. Important: The content of the linked component is duplicated from the source view you\'re currently editing. If you initiate the link while editing the "description" component of the "My Product Share A" view, the "description" component in "My Product Share A" is copied to the "My Product Share B" and will thus override the "description" component of "My Product Share B".',
    linkThroughShareClassesForbiddenCrossProductTooltip:
      "This component displays data for all share classes, so it is always linked across every share class of the product. You cannot unlink individual share classes for this reason; only links to other views can be removed.",
    linkedWithViews: "Linked with the following views",
    linkedWithShareClasses: "Linked with the following share classes",
    view: "View",
    shareClass: "Share class",
    title: (ctx) => `Edit linking of ${ctx.named("componentId")}`,
    unlink: "Unlink",
    unlinking: "Unlinking ...",
    unsavedChangesAlert:
      "You have unsaved changes. If you leave without saving, they will be lost.",
    confirmSaveDialog: {
      computingNewLinks: "Computing new links...",
      onceLinked:
        "Once linked, when a change is applied to one component in any of those locations (including the current one), all the other linked components will be updated in the same way. Please confirm that you wish to proceed with saving.",
      overwrittenExistingComponent: "Overwriting a component with the same id",
      overwrittenExistingComponentTooltip: (ctx) =>
        `Warning: There is already a component with ID '${ctx.named(
          "componentId"
        )}' at this location, so it will be overwritten by this component if you link it.`,
      title: "Confirm save of new links",
      youAreAboutToLink:
        "You are about to link the current component with the components in the following locations:",
    },
    overviewStyle: {
      title: "Edit links in the style of the overview",
      saveSuccess: "The update of the style links was successful.",
    },
    reportFooter: {
      title: "Edit links in the footer of the report",
      saveSuccess: "The update of the footer links was successful.",
    },
    reportHeader: {
      title: "Edit links in the header of the report",
      saveSuccess: "The update of the header links was successful.",
    },
    reportStyle: {
      title: "Edit links in the style of the report",
      saveSuccess: "The update of the style links was successful.",
    },
    notify: {
      linkSaveSuccess: (ctx) =>
        `The linking update of '${ctx.named("componentId")}' was successful.`,
    },
    multiselect: {
      placeholderShareClasses: "Select share classes to link",
      placeholderViews: "Select views to link",
      selectLabel: "Press enter to select",
      deselectLabel: "Press enter to remove",
      selectedLabel: "Selected",
    },
    overviewHeader: {
      title: "Edit linking of overview header",
      saveSuccess: "The linking update of the header was successful.",
    },
  },
  pricesManagement: {
    addData: "Add data",
    autoDataUpdate: "Your data is updated automatically.",
    date: "Date",
    datePlaceholder: "YYYY-MM-DD",
    dateValidationError: "You can't add a date that already has a value.",
    exportCsvButton: "Export as CSV",
    importCsvButton: "Import from CSV",
    launchPriceUpdateButton: "Launch update",
    launchPriceUpdateTooltip:
      "Clicking this button forces an update by fetching the latest data from the source normally used for the automatic daily update (if this data is automatically updated). It can take a few minutes for the data to be fetched and updated.",
    launchPriceUpdateRunningTooltip: "The prices are currently being fetched.",
    loadingData: "Loading data...",
    price: "Price",
    priceAdj: "Price (adjusted)",
    priceAdjTooltip:
      "Generally, adjusted prices mean prices that are adjusted for dividends, splits, and other events. If not provided, it is assumed to be the same as the price.",
    priceAdjTooltipTable:
      "Generally, adjusted prices mean prices that are adjusted for dividends, splits, and other events.",
    refreshButton: "Refresh",
    removeData: "Remove data",
    benchmarks: {
      noBenchmark: "No benchmark available for this product.",
    },
    dialog: {
      removeData: {
        title: "Remove a data point",
        text: "You are going to completely remove a data point. Are you sure?",
      },
    },
    importCsvText: {
      para1:
        "The input file is expected to be in CSV format, with two or three columns and a header row.",
      para2:
        "The first column should consist of dates, and the second column should consist of numbers (the price at the corresponding date). A third column is optional, and if given it should consist of numbers corresponding to the adjusted price; if it is not given the adjusted prices are assumed to be the same as the prices.",
      para3:
        "Please ensure that your CSV is valid to avoid any issues when reading and parsing the data.",
    },
    notify: {
      dateUsedMultipleTimeError:
        "There are multiple prices associated with one date. You have to ensure that each date has a unique price assigned to it.",
      launchSuccessText:
        "The price update was launched successfully. Please refresh in a few minutes to see the results.",
      launchErrorText: "Error when launching the price update, please contact us.",
      nanFound:
        "Some empty values were found, please make sure that all rows and columns contain a value.",
      numberFormatError:
        'At least one of the numbers has an unrecognized format. Ideally, you should not use any separators for thousands and use a dot for the decimal separator as this is the expected format. For example, "102.12", "1380.25", "18.952", ...',
      saveErrorText: "Error when updating prices, please contact us.",
      saveSuccessText: "The prices were updated successfully.",
    },
    prompt: {
      addPrice: "Add a price",
      importCsv: "Import data from CSV",
    },
  },
  tables: {
    metricsTable: {
      na: "N/A",
      name: "Name",
      showLess: "Show less",
      showMore: "Show more",
    },
    monthlyReturnsTable: {
      benchmark: "Benchmark",
      return: "Return",
    },
    performanceTable: {
      computedFromTo: (ctx) =>
        `Performance computed from ${ctx.named("firstDate")} to ${ctx.named("lastDate")}.`,
      notAvailableData:
        "Data not available either because there isn't enough days to compute it, the data is not meaningful (e.g. negative sharpe ratio), or the benchmark is not always available during the selected period",
    },
    holdingsTable: {
      noDataYet: "This component has no data yet.",
      holdingFields: {
        currency: "Currency",
        holdingName: "Name",
        isin: "ISIN",
        value: "Value",
        weight: "Weight",
      },
      totalOfPortfolio: "Total of portfolio",
      holdingsCountSummary: (ctx) => `${ctx.named("shown")} out of ${ctx.named("total")} holdings`,
    },
    liquidityAnalysisTable: {
      noDataYet: "This component has no data yet.",
      description: "Description",
      isin: "ISIN",
      currency: "Currency",
      aumInLocalCurrency: (ctx) => `AUM (${ctx.named("currency")})`,
      weight: "Weight",
      lessThanOneDay: "0-1 day",
      twoToSevenDays: "2-7 days",
      eightToThirtyDays: "8-30 days",
      thirtyOneToNinetyDays: "31-90 days",
      ninetyOneToOneHundredEightyDays: "91-180 days",
      oneHundredEightyOneToThreeHundredSixtyFiveDays: "181-365 days",
      moreThanThreeHundredSixtyFiveDays: ">365 days",
      totalOfPortfolio: "Total of portfolio",
    },
  },
  thirdParty: {
    vuePhoneNumberInput: {
      countrySelectorLabel: "Country code",
      countrySelectorError: "Select a country",
      phoneNumberLabel: "Phone number",
      example: "Example :",
    },
  },
  videos: {
    uploadVideo: {
      file: "File",
      noFile: "No file",
      noVideo: "No video",
      selectFile: "Select file",
      selectNewVideo: "Select new video",
      errorTitleTooLarge: "Video too large",
      errorMessageTooLarge:
        "The video is too large. Please, select another video with a size smaller than 30 MB.",
      warningTitleVeryLarge: "Video very large",
      warningMessageVeryLarge:
        "The video is very large. It will make the pages displaying this video slower to load. For best performance, it is advised to use videos smaller than 10 MB.",
    },
  },
};
